import { forwardRef } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";

/**
 * Card — Anthropic Claude Peach uslubi.
 * R4 polish: variantlar default/elevated/featured/ghost, paddings compact/default/comfortable.
 * "aurora" variant — backward compat alias (featured kabi ishlaydi).
 *
 * Tokens: --bg-surface, --bg-elevated, --border-subtle, --border-strong,
 *          --brand-primary, --brand-glow, --shadow-soft.
 */
const cardVariants = cva(
  "relative rounded-xl text-[var(--text-primary)] transition-colors duration-150",
  {
    variants: {
      variant: {
        default: cn(
          "bg-[var(--bg-surface)] border border-[var(--border-subtle)]",
          "hover:border-[var(--border-strong)]",
        ),
        elevated: cn(
          "bg-[var(--bg-elevated)] border border-[var(--border-subtle)]",
          "shadow-[var(--shadow-soft)]",
          "hover:border-[var(--border-strong)]",
        ),
        // Featured — coral primary border 40% + brand glow (Anthropic featured kartochka)
        featured: cn(
          "bg-[var(--bg-surface)] rounded-2xl",
          "border border-[var(--brand-primary)]/40",
          "shadow-[var(--brand-glow)]",
          "hover:border-[var(--brand-primary)]/60",
        ),
        ghost: cn(
          "bg-transparent border border-[var(--border-subtle)]",
          "hover:border-[var(--border-strong)]",
        ),
        // Backward compat — eski "aurora" variant featured kabi ishlaydi
        aurora: cn(
          "bg-[var(--bg-surface)] rounded-2xl",
          "border border-[var(--brand-primary)]/40",
          "shadow-[var(--brand-glow)]",
          "hover:border-[var(--brand-primary)]/60",
        ),
      },
      padding: {
        none: "",
        // Yangi semantic ladder
        compact: "p-4",
        default: "p-6",
        comfortable: "p-8",
        // Backward compat (eski sm/md/lg)
        sm: "p-3",
        md: "p-4",
        lg: "p-6",
      },
    },
    defaultVariants: { variant: "default", padding: "none" },
  },
);

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, padding, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(cardVariants({ variant, padding }), className)}
      {...props}
    />
  ),
);
Card.displayName = "Card";

export const CardHeader = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("flex flex-col gap-1.5 px-6 pt-6 pb-3", className)}
      {...props}
    />
  ),
);
CardHeader.displayName = "CardHeader";

export const CardTitle = forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn(
        "font-semibold tracking-tight text-[var(--text-primary)]",
        "text-[18px] leading-[26px]",
        "font-[family-name:var(--font-display,'Inter_Tight_Variable',_'Inter_Variable',_system-ui)]",
        className,
      )}
      {...props}
    />
  ),
);
CardTitle.displayName = "CardTitle";

export const CardDescription = forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn(
      "text-[14px] leading-[22px] text-[var(--text-secondary)]",
      className,
    )}
    {...props}
  />
));
CardDescription.displayName = "CardDescription";

export const CardContent = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("px-6 py-3", className)} {...props} />
  ),
);
CardContent.displayName = "CardContent";

export const CardFooter = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "flex items-center gap-2 px-6 pb-6 pt-3 border-t border-[var(--border-subtle)]/60",
        className,
      )}
      {...props}
    />
  ),
);
CardFooter.displayName = "CardFooter";
